import { tr } from "@/i18n/copy";
export const TENANT_WORKSPACE_ROLES = new Set([
  "studio_owner",
  "manager",
  "agent_marketing",
  "artist",
  "front_desk",
]);

export function workspacePathForRole(role) {
  const paths = {
    studio_owner: "/app/owner",
    manager: "/agent/manager",
    agent_marketing: "/agent/marketing",
    artist: "/app/artist",
    front_desk: "/app/front-desk",
  };
  return paths[role] || null;
}

export function applicationPathForRole(role) {
  if (role === "studio_owner") return "/app";
  if (role === "client") return "/client";
  if (role === "platform_admin") return "/admin";
  return workspacePathForRole(role);
}

export async function fetchWorkspaceBootstrap(client, role, config) {
  if (!TENANT_WORKSPACE_ROLES.has(role)) return null;
  return config ? client.get("/workspace/me", config) : client.get("/workspace/me");
}

export async function fetchClientContext(client, role) {
  if (role !== "client") return null;
  return client.get("/client/me");
}

export function registerServiceWorker() {
  if (process.env.NODE_ENV === "test" || !("serviceWorker" in navigator)) return;
  window.addEventListener("load", () => {
    navigator.serviceWorker.register(`${process.env.PUBLIC_URL || ""}/service-worker.js`).catch(() => {
      // PWA enhancement must never block the authenticated web application.
    });
  });
}

export function pushPlatformSupport() {
  const nav = typeof navigator !== "undefined" ? navigator : {};
  const ua = nav.userAgent || "";
  const isiOS = /iPhone|iPad|iPod/i.test(ua) || (nav.platform === "MacIntel" && Number(nav.maxTouchPoints || 0) > 1);
  const standalone = typeof window !== "undefined" && (
    window.matchMedia?.("(display-mode: standalone)")?.matches || nav.standalone === true
  );
  const apiSupported = typeof window !== "undefined" && "Notification" in window && "serviceWorker" in nav && "PushManager" in window;
  return {
    isiOS,
    standalone,
    apiSupported,
    canEnable: apiSupported && (!isiOS || standalone),
    reason: isiOS && !standalone ? "ios_home_screen_required" : (!apiSupported ? "unsupported" : null),
  };
}


function vapidKeyBytes(value) {
  const padded = value.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(value.length / 4) * 4, "=");
  const raw = window.atob(padded);
  return Uint8Array.from(raw, (character) => character.charCodeAt(0));
}

const PUSH_STATE_EVENT = "tps:push-state-changed";
const preferenceKey = (scope) => `tps:push-enabled:v1:${scope}`;
function pushPreference(scope) {
  try { return localStorage.getItem(preferenceKey(scope)); } catch { return null; }
}
function rememberPush(scope, enabled) {
  if (!scope) return;
  try { localStorage.setItem(preferenceKey(scope), enabled ? "true" : "false"); } catch {}
}
function pushChanged() { window.dispatchEvent(new Event(PUSH_STATE_EVENT)); }

// A missing worker/network connection must not leave a settings panel stuck forever.
async function pushRegistration() {
  let timer;
  try {
    return await Promise.race([
      navigator.serviceWorker.ready,
      new Promise((_, reject) => { timer = setTimeout(() => reject(new Error(tr("Notifiche temporaneamente non disponibili. Riprova."))), 10000); }),
    ]);
  } finally { clearTimeout(timer); }
}

export async function getPushDeviceState(api, relationshipId = null) {
  const permission = window.Notification?.permission || "unsupported";
  const subscription = pushPlatformSupport().canEnable
    ? await (await pushRegistration()).pushManager.getSubscription() : null;
  const status = await api.post("/push/device-status", {
    endpoint: subscription?.endpoint || "", relationship_id: relationshipId || undefined,
  });
  return { ...status, permission, subscription,
    device_active: Boolean(subscription && permission === "granted" && status.device_active) };
}

// Serialize endpoint changes across settings panels, resume events and Studio changes.
let pushOperation = Promise.resolve();
function changePush(operation) {
  const result = pushOperation.then(operation);
  pushOperation = result.catch(() => {});
  return result;
}

async function registerPush(api, relationshipId, state, isCurrent = () => true) {
  if (!state.configured || !state.push_enabled || !isCurrent()) return false;
  let subscription = state.subscription;
  const replacesEndpoint = state.endpoint_expired ? subscription?.endpoint : undefined;
  if (replacesEndpoint) {
    await subscription.unsubscribe();
    subscription = null;
  }
  if (!subscription) {
    let publicKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
    if (!publicKey) publicKey = (await api.get("/push/public-key"))?.public_key;
    if (!publicKey || !isCurrent()) return false;
    const registration = await pushRegistration();
    subscription = await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: vapidKeyBytes(publicKey) });
  }
  if (!isCurrent()) return false;
  const result = await api.post("/push/subscriptions", {
    endpoint: subscription.endpoint, keys: subscription.toJSON().keys,
    relationship_id: relationshipId || undefined, expected_user_id: state.user_id,
    ...(replacesEndpoint ? { replaces_endpoint: replacesEndpoint } : {}),
  });
  rememberPush(result.scope_key || state.scope_key, true);
  pushChanged();
  return true;
}

export async function enablePushNotifications(api, relationshipId = null) {
  if (!pushPlatformSupport().canEnable || Notification.permission === "denied") return false;
  // Request in the click gesture, before network work; never re-prompt a granted permission.
  const permission = Notification.permission === "granted" ? "granted" : await Notification.requestPermission();
  if (permission !== "granted") return false;
  return changePush(async () => registerPush(api, relationshipId, await getPushDeviceState(api, relationshipId)));
}

export async function disablePushNotifications(api, relationshipId = null) {
  if (!("serviceWorker" in navigator)) return false;
  return changePush(async () => {
    const state = await getPushDeviceState(api, relationshipId);
    if (state.subscription) {
      const result = await api.post("/push/subscriptions/revoke", {
        endpoint: state.subscription.endpoint, keys: state.subscription.toJSON().keys,
        relationship_id: relationshipId || undefined, expected_user_id: state.user_id,
      });
      // Revoking this Studio must preserve other authorized audiences on the browser.
      if (result.unsubscribe) await state.subscription.unsubscribe();
    }
    rememberPush(state.scope_key, false);
    pushChanged();
    return true;
  });
}

export async function restorePushNotifications(api, relationshipId = null, expectedUserId = null, isCurrent = () => true) {
  if (!pushPlatformSupport().canEnable || Notification.permission !== "granted") return false;
  return changePush(async () => {
    if (!isCurrent()) return false;
    const state = await getPushDeviceState(api, relationshipId);
    if (!isCurrent() || (expectedUserId && state.user_id !== expectedUserId)) return false;
    const preference = pushPreference(state.scope_key);
    // Permission alone is not opt-in for a new account or another Studio.
    if (preference === "false" || (preference !== "true" && !state.device_recoverable)) return false;
    if (state.device_active) { rememberPush(state.scope_key, true); return true; }
    return registerPush(api, relationshipId, state, isCurrent);
  });
}

export function watchPushNotifications(api, userId, relationshipId = null) {
  let stopped = false;
  let lastAttempt = 0;
  const restore = () => {
    if (stopped || document.visibilityState === "hidden" || Date.now() - lastAttempt < 60000) return;
    lastAttempt = Date.now();
    restorePushNotifications(api, relationshipId, userId, () => !stopped).catch(() => {});
  };
  restore();
  window.addEventListener("focus", restore);
  window.addEventListener("online", restore);
  window.addEventListener("pageshow", restore);
  document.addEventListener("visibilitychange", restore);
  return () => {
    stopped = true;
    window.removeEventListener("focus", restore);
    window.removeEventListener("online", restore);
    window.removeEventListener("pageshow", restore);
    document.removeEventListener("visibilitychange", restore);
  };
}

export function safeNotificationRoute(route) {
  return typeof route === "string" && route.startsWith("/") && !route.startsWith("//") && !route.includes("\\") ? route : null;
}
